import { useEffect, useState } from 'react';
import { apiService } from '../services/api';

// Every page mounts its own NavigationBar, so cache the list briefly to avoid refetching on each route change.
const CACHE_TTL_MS = 60 * 1000;
let cache = { at: 0, events: null, promise: null };
// Bumped on every invalidation so an in-flight response from before it cannot repopulate the cache
let generation = 0;

export const invalidateMyEventsCache = () => {
    generation += 1;
    cache = { at: 0, events: null, promise: null };
};

const loadMyEvents = () => {
    if (cache.events && Date.now() - cache.at < CACHE_TTL_MS) {
        return Promise.resolve(cache.events);
    }
    if (!cache.promise) {
        const startedAt = generation;
        cache.promise = apiService.getMyEvents()
            .then((response) => {
                const events = Array.isArray(response.data) ? response.data : [];
                if (startedAt === generation) {
                    cache = { at: Date.now(), events, promise: null };
                }
                return events;
            })
            .catch((err) => {
                if (startedAt === generation) cache.promise = null;
                throw err;
            });
    }
    return cache.promise;
};

/**
 * Events visible to the signed-in contact, with their registration status.
 * Returns { events, loading, error }.
 */
const useMyEvents = ({ enabled = true } = {}) => {
    const [events, setEvents] = useState(cache.events || []);
    const [loading, setLoading] = useState(enabled && !cache.events);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        setLoading(!cache.events);
        loadMyEvents()
            .then((list) => { if (!cancelled) { setEvents(list); setError(null); } })
            .catch((err) => { if (!cancelled) setError(err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [enabled]);

    return { events, loading, error };
};

export default useMyEvents;
